import { DiscoveryApi, FetchApi, discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { FieldExtensionComponentProps, createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { JsonObject } from '@backstage/types';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { getGcpProjectIdFromForm } from './utils';

type ServiceAccount = {
  email: string;
  displayName?: string;
};

export async function loadServiceAccounts(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  projectId: string,
): Promise<ServiceAccount[]> {
  const baseUrl = await discoveryApi.getBaseUrl('gcp');
  const res = await fetchApi.fetch(
    `${baseUrl}/projects/${encodeURIComponent(projectId)}/service-accounts`,
    { credentials: 'include' },
  );
  if (!res.ok) throw new Error(`Failed to load service accounts (${res.status})`);
  const body = await res.json();
  return Array.isArray(body?.serviceAccounts) ? body.serviceAccounts : [];
}

const saName = (email: string) => email.split('@')[0];

export const GCPServiceAccountField = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
}: FieldExtensionComponentProps<string[]>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const projectId = getGcpProjectIdFromForm(formContext?.formData as JsonObject | undefined);
  const [accounts, setAccounts] = useState<ServiceAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | undefined>(undefined);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const prevProjectIdRef = useRef(projectId);

  // Clear selected SAs when the project changes (skip initial mount)
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;
      onChangeRef.current([]);
    }
  }, [projectId]);

  // Fetch service accounts for the current project
  useEffect(() => {
    if (!projectId) {
      setAccounts([]);
      setErr(undefined);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setErr(undefined);

    loadServiceAccounts(discoveryApi, fetchApi, projectId)
      .then(data => { if (!cancelled) setAccounts(data); })
      .catch(e => { if (!cancelled) setErr(e?.message ?? 'Failed to load service accounts'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, discoveryApi, fetchApi]);

  const selected: string[] = formData ?? [];

  const handleChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    onChange((e.target.value as string[]) ?? []);
  };

  return (
    <FormControl margin="normal" required={required} error={(rawErrors?.length ?? 0) > 0 && required && selected.length === 0}>
      <InputLabel id="gcpServiceAccount-label">Service accounts (optional)</InputLabel>
      <Select
        labelId="gcpServiceAccount-label"
        id="gcpServiceAccount"
        multiple
        value={selected}
        onChange={handleChange}
        disabled={!projectId || loading}
        renderValue={vals => (vals as string[]).map(saName).join(', ')}
      >
        {accounts.map(sa => (
          <MenuItem key={sa.email} value={sa.email}>
            <Checkbox checked={selected.includes(sa.email)} />
            <ListItemText
              primary={saName(sa.email)}
              secondary={sa.displayName}
            />
          </MenuItem>
        ))}
      </Select>
      {loading ? <CircularProgress size={18} /> : null}
      <FormHelperText>
        {!projectId
          ? 'Select a GCP project first'
          : `Grant roles/storage.objectAdmin to existing service accounts${err ? ` — ${err}` : ''}`}
      </FormHelperText>
    </FormControl>
  );
};

export const GcpServiceAccountFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GCPServiceAccountField',
    component: GCPServiceAccountField,
  }),
);
